import { Input, Modal, SubmitButton, Table } from "@xola/ui-kit";
import { isEquipmentBased, requiredString } from "common";
import { sortBy } from "lodash";
import React from "react";
import {
    AdjustmentOperation,
    AdjustmentType,
    AllowedPrivacy,
    DemographicPrice,
    Experience,
    PriceVariation,
    PriceVariationType,
    PricingStructure,
    PricingTermType,
    PricingType,
    PricingTypeCopy,
    ProductCatalogItem,
    UnitType,
} from "types";
import { array, number, object } from "yup";
import { getCatalogItemsByUnitType } from "../../../models/Catalog";
import {
    calculatePriceWithAdjustment,
    getDemographicPricesForExperienceCatalog,
} from "../../../models/DemographicPrice";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { DemographicPriceItem } from "./DemographicPriceItem";

interface PriceVariationModalProps {
    title: string;
    isEditPriceVariation: boolean;
    isOpen: boolean;
    experience: Experience;
    priceVariation: PriceVariation;
    onClose: () => void;
    onSubmit: (priceVariation: PriceVariation) => void;
}

export const PriceVariationModal = ({
    title,
    isEditPriceVariation = false,
    isOpen,
    experience,
    priceVariation,
    onClose,
    onSubmit,
}: PriceVariationModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose} onSubmit={onsubmit}>
            <ModalContent
                isEditPriceVariation={isEditPriceVariation}
                title={title}
                experience={experience}
                priceVariation={priceVariation}
                onClose={onClose}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

type ModalContentProps = Omit<PriceVariationModalProps, "isOpen">;

const isAmountValid = (value: number) => {
    if (value <= 0) {
        return false;
    }

    const pattern = /^[+-]?\d+(\.\d{0,2})?$/;
    return pattern.test(value.toString());
};

const validationSchema = object({
    name: requiredString(),
    public: array(
        object({
            adjustmentAmount: number()
                .typeError("invalid number")
                .test("amountCheck", "Must be a positive number with upto two decimal places", (value) =>
                    value ? isAmountValid(value) : true,
                ),
        }),
    ),
    private: array(
        object({
            adjustmentAmount: number()
                .typeError("invalid number")
                .test("amountCheck", "Must be a positive number with upto two decimal places", (value) =>
                    value ? isAmountValid(value) : true,
                ),
        }),
    ),
});

const ModalContent = ({
    isEditPriceVariation,
    title,
    experience,
    priceVariation,
    onClose,
    onSubmit,
}: ModalContentProps) => {
    const isEquipment = isEquipmentBased(experience);
    const initialValues = getInitialValues(experience, priceVariation, isEditPriceVariation, isEquipment);
    const privatePricingGroup = priceVariation.pricingGroups.find(
        (pricingGroup) => pricingGroup.privacy === AllowedPrivacy.PRIVATE,
    );

    const handleFormSubmit = (values: { public: DemographicPrice[]; private: DemographicPrice[]; name: string }) => {
        const updatedPriceVariation = {
            ...priceVariation,
            name: values.name,
            type: PriceVariationType.ADJUSTMENT,
            pricingGroups: priceVariation.pricingGroups.map((priceGroup) => {
                return {
                    ...priceGroup,
                    ...(priceGroup.privacy === AllowedPrivacy.PRIVATE &&
                        priceGroup.pricingStructure === PricingStructure.BUYOUT && {
                            pricingStructure: PricingStructure.FLAT,
                        }),
                    terms: [
                        {
                            demographicPrices:
                                priceGroup.privacy === AllowedPrivacy.PUBLIC ? values.public : values.private,
                            type: PricingTermType.ADJUSTMENT,
                        },
                    ],
                };
            }),
        };
        onSubmit(updatedPriceVariation);
    };

    return (
        <XolaFormik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
            {({ handleSubmit, values, setFieldValue, errors }) => (
                <>
                    <Modal.Header>{title}</Modal.Header>

                    <Modal.Body>
                        <div className="space-y-4">
                            <div className="w-full">
                                <span className="w-full text-sm font-bold">Name</span>
                                <Input
                                    required
                                    className="border-gray-light"
                                    value={values.name}
                                    isError={errors.name ?? false}
                                    onChange={(event: any) => setFieldValue("name", event.target.value)}
                                />
                            </div>

                            {values.public.length > 0 && (
                                <DemographicPriceTable
                                    values={values.public}
                                    pricingType={PricingType.PER_PERSON}
                                    privacy={AllowedPrivacy.PUBLIC}
                                    isEquipment={isEquipment}
                                    errors={errors}
                                    onChange={(key, value) => setFieldValue(`public.${key}`, value)}
                                />
                            )}

                            {values.private.length > 0 && privatePricingGroup && (
                                <DemographicPriceTable
                                    values={values.private}
                                    pricingType={privatePricingGroup.pricingType}
                                    privacy={AllowedPrivacy.PRIVATE}
                                    isEquipment={isEquipment}
                                    errors={errors}
                                    onChange={(key, value) => setFieldValue(`private.${key}`, value)}
                                />
                            )}
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton type="submit" onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface DemographicPriceTableProps {
    values: DemographicPrice[];
    pricingType: PricingType;
    privacy: AllowedPrivacy;
    isEquipment: boolean;
    errors: any;
    onChange: (key: string, value: any) => void;
}

const DemographicPriceTable = ({
    values,
    pricingType,
    onChange,
    privacy,
    isEquipment,
    errors,
}: DemographicPriceTableProps) => {
    return (
        <div className="flex flex-col space-y-2">
            <div className="text-sm font-bold">
                <span>
                    Terms - {PricingTypeCopy[pricingType].toLowerCase()} {privacy} pricing
                </span>
            </div>

            {pricingType === PricingType.PER_PERSON && (
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>{isEquipment ? "Equipment" : "Demographic"}</Table.Header>
                            <Table.Header>Base Price</Table.Header>
                            <Table.Header>Adjustment</Table.Header>
                            <Table.Header>Final Price</Table.Header>
                        </Table.Row>
                    </Table.Head>

                    <Table.Body isStriped={false}>
                        {values.map((demographicPrice, index) => {
                            return (
                                <DemographicPriceItem
                                    key={demographicPrice.template.id}
                                    demographicPrice={demographicPrice}
                                    errors={privacy === AllowedPrivacy.PUBLIC ? errors.public : errors.private}
                                    index={index}
                                    setFieldValue={onChange}
                                />
                            );
                        })}
                    </Table.Body>
                </Table>
            )}

            {pricingType === PricingType.PER_OUTING && (
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Base Price</Table.Header>
                            <Table.Header>Adjustment</Table.Header>
                            <Table.Header>Final Price</Table.Header>
                        </Table.Row>
                    </Table.Head>

                    <Table.Body isStriped={false}>
                        {values.map((perOutingPrice, index) => {
                            return (
                                <DemographicPriceItem
                                    key={perOutingPrice.template.id}
                                    perOuting
                                    demographicPrice={perOutingPrice}
                                    errors={privacy === AllowedPrivacy.PUBLIC ? errors.public : errors.private}
                                    index={index}
                                    setFieldValue={onChange}
                                />
                            );
                        })}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

const getInitialValues = (
    experience: Experience,
    priceVariation: PriceVariation,
    isEditPriceVariation: boolean,
    isEquipment: boolean,
) => {
    return {
        name: priceVariation.name,
        public: sortBy(
            getDemographicPrices(priceVariation, AllowedPrivacy.PUBLIC, experience, isEditPriceVariation, isEquipment),
            "catalogItem.sequence",
        ),
        private: sortBy(
            getDemographicPrices(priceVariation, AllowedPrivacy.PRIVATE, experience, isEditPriceVariation, isEquipment),
            "catalogItem.sequence",
        ),
    };
};

const getDemographicPrices = (
    priceVariation: PriceVariation,
    privacy: AllowedPrivacy,
    experience: Experience,
    isEditPriceVariation: boolean,
    isEquipment: boolean,
) => {
    const pricingGroup = priceVariation.pricingGroups.find((pricingGroup) => pricingGroup.privacy === privacy);
    let demographicPrices: DemographicPrice[] = [];
    if (pricingGroup) {
        const existingPrices = pricingGroup.terms[0].demographicPrices;

        if (isEquipment) {
            // Prices already have price + adjustments from the merged base+variation rules.
            // Attach catalogItem for name display. Filter rows with no price — a pricingGroup
            // can exist (e.g. public) without equipment pricing when that privacy isn't used.
            const equipmentCatalogItems = getCatalogItemsByUnitType(
                experience.catalog?.items ?? [],
                UnitType.EQUIPMENT,
            ) as ProductCatalogItem[];
            demographicPrices = existingPrices
                .filter((p) => p.price !== undefined)
                .map((p) => ({
                    ...p,
                    catalogItem: equipmentCatalogItems.find((c) => c.template?.id === p.template?.id) ?? p.catalogItem,
                }));
        } else {
            demographicPrices =
                pricingGroup.pricingType === PricingType.PER_PERSON &&
                !pricingGroup.terms[0].demographicPrices[0].catalogItem
                    ? getDemographicPricesForExperienceCatalog(experience, pricingGroup.terms[0])
                    : pricingGroup.terms[0].demographicPrices;
        }

        if (privacy === AllowedPrivacy.PRIVATE && !isEditPriceVariation) {
            if (pricingGroup.pricingStructure === PricingStructure.RELATIVE) {
                // For Relative Pricing, the pricing rule will have adjustment instead of base, so we need to add the base price of Public pricing + the adjustment of relative pricing
                // and show it as base price in price variation modal
                // This will get applied only for new price variation, for edit price variation we calculate baseprice of relative pricing before hand since
                // the adjustment values will have price variation values instead of relative pricing values

                const publicPricingGroup = priceVariation.pricingGroups.find(
                    (pricingGroup) => pricingGroup.privacy === AllowedPrivacy.PUBLIC,
                );
                const publicPricingDemographicPrices = publicPricingGroup?.terms[0].demographicPrices ?? [];

                for (const demographicPrice of demographicPrices) {
                    const baseDemographicPrice = publicPricingDemographicPrices.find((publicDemographicPrice) => {
                        return publicDemographicPrice.template.id === demographicPrice.template.id;
                    });
                    const basePrice = calculatePriceWithAdjustment(baseDemographicPrice?.price ?? 0, demographicPrice);
                    demographicPrice.price = basePrice ?? 0;
                    delete demographicPrice.adjustmentAmount;
                    delete demographicPrice.adjustmentOperation;
                    delete demographicPrice.adjustmentType;
                }
            } else if (pricingGroup.pricingStructure === PricingStructure.BUYOUT) {
                const publicPricingGroup = priceVariation.pricingGroups.find(
                    (pricingGroup) => pricingGroup.privacy === AllowedPrivacy.PUBLIC,
                );

                if (publicPricingGroup) {
                    const publicPrices = publicPricingGroup.terms[0].demographicPrices;
                    const publicHasCatalogItem = publicPrices.length > 0 && publicPrices[0].catalogItem;
                    demographicPrices =
                        publicPricingGroup.pricingType === PricingType.PER_PERSON && !publicHasCatalogItem
                            ? getDemographicPricesForExperienceCatalog(experience, publicPricingGroup.terms[0])
                            : publicPrices;
                }
            }
        }

        demographicPrices = addAdjustmentValues(demographicPrices);
    }

    return demographicPrices;
};

const addAdjustmentValues = (demographicPrices: DemographicPrice[]) => {
    for (const demographicPrice of demographicPrices) {
        demographicPrice.adjustmentAmount = demographicPrice.adjustmentAmount ?? 0;
        demographicPrice.adjustmentType = demographicPrice.adjustmentType ?? AdjustmentType.ABSOLUTE;
        demographicPrice.adjustmentOperation = demographicPrice.adjustmentOperation ?? AdjustmentOperation.MARKUP;
    }

    return demographicPrices;
};
